import React, { useEffect, useRef } from "react";
import riderArt from "../../../assets/mrmilliyorider.png";

/* ─── Brand palette ─────────────────────────────────────────── */
const BRAND = {
  forest: "#0E3A2C", pine: "#1A5440", gold: "#C9952A", leaf: "#6E9039",
  honey: "#E8C96A", cream: "#FDF6E3", sand: "#F1E6C9", ink: "#15281D",
};

/* ─── Load Leaflet CSS + JS once (same CDN build the Location page uses) ─── */
const ensureLeafletLoaded = () => {
  if (!document.getElementById("leaflet-css")) {
    const link = document.createElement("link");
    link.id   = "leaflet-css";
    link.rel  = "stylesheet";
    link.href = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
    document.head.appendChild(link);
  }
  return new Promise((resolve) => {
    if (window.L) { resolve(window.L); return; }
    const existing = document.querySelector('script[src*="leaflet.min.js"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(window.L));
      return;
    }
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
    script.onload = () => resolve(window.L);
    document.head.appendChild(script);
  });
};

/* ─── Marker geometry: a circular brand token with a pointer tail ─── */
const RING    = 52;
const TAIL    = 11;
const TAIL_Y  = RING - 3;          /* the tail tucks under the ring          */
const ICON_W  = RING;
const ICON_H  = TAIL_Y + TAIL;     /* anchor sits exactly on the tail tip    */

const GLIDE_MS   = 800;   /* how long the mascot takes to slide to a new fix */
const MAX_TILT   = 16;    /* he leans into the climb — never past upright    */
const MIN_MOVE_M = 4;     /* below this the fix is GPS noise, not a heading  */
const DASH_SPAN  = 18;    /* dashArray "8 10" — one full dash + gap          */
const SELF_VIEW_MS = 900; /* how long a view change of ours stays disowned   */

const toRad = (deg) => (deg * Math.PI) / 180;

const reducedMotion = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* Compass bearing (0 = north, 90 = east) between two fixes */
const bearingBetween = (from, to) => {
  const dLng = toRad(to.lng - from.lng);
  const y = Math.sin(dLng) * Math.cos(toRad(to.lat));
  const x = Math.cos(toRad(from.lat)) * Math.sin(toRad(to.lat)) -
            Math.sin(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

/* Leaflet drives the icon's translate3d — a transition on it turns every
   setLatLng into a glide instead of a teleport. */
const glide = (el, on) => {
  if (!el) return;
  el.style.transition = on && !reducedMotion() ? `transform ${GLIDE_MS}ms linear` : "";
};

/* The mascot is drawn facing left, so he is mirrored when heading east and
   leans into the climb. Only the art turns — the gold ring stays upright. */
const applyHeading = (el, deg) => {
  const rot = el && el.querySelector(".mco-rot");
  if (!rot) return;
  const mirrored = deg > 0 && deg < 180;
  const tilt = (mirrored ? -1 : 1) * MAX_TILT * Math.cos(toRad(deg));
  rot.style.transform = `rotate(${tilt.toFixed(1)}deg) scaleX(${mirrored ? -1 : 1})`;
};

/* The artwork is an opaque square with white corners — the circular clip is
   what turns it into a usable map token. */
const COURIER_HTML = `
  <div class="mco-body">
    <span class="mco-pulse"></span>
    <div class="mco-ring">
      <div class="mco-rot"><img class="mco-art" src="${riderArt}" alt="" /></div>
    </div>
    <span class="mco-tail"></span>
  </div>`;

const DEST_HTML = `
  <div class="mco-dest">
    <svg viewBox="0 0 30 38" width="30" height="38" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 0C6.716 0 0 6.716 0 15c0 9.9 13.5 22.34 14.08 22.87a1.37 1.37 0 0 0 1.84 0C16.5 37.34 30 24.9 30 15 30 6.716 23.284 0 15 0z" fill="${BRAND.forest}"/>
      <circle cx="15" cy="14.6" r="8.4" fill="${BRAND.cream}"/>
      <path d="M15 9.4 9.6 14.6h1.7v5.4h7.4v-5.4h1.7z" fill="${BRAND.forest}"/>
      <rect x="13.8" y="16.4" width="2.4" height="3.6" fill="${BRAND.cream}"/>
    </svg>
  </div>`;

const MAP_CSS = `
.mco-marker, .mco-destmarker { pointer-events: none; }
.mco-marker { will-change: transform; }
.mco-body { position: relative; width: 100%; height: 100%; }
.mco-ring {
  position: absolute; left: 0; top: 0;
  width: ${RING}px; height: ${RING}px;
  box-sizing: border-box;
  /* No clip, border or fill: the artwork is a transparent PNG that already
     carries its own circular badge and green ring. Clipping it here would
     shave that ring and draw a second border around it. The shadow is a
     drop-shadow filter rather than box-shadow so it follows the artwork's
     alpha instead of boxing it. */
  filter: drop-shadow(0 5px 9px rgba(21,40,29,0.38));
}
.mco-rot {
  width: 100%; height: 100%;
  transform-origin: 50% 50%;
  transition: transform 420ms ease-out;
}
.mco-art {
  /* contain, not cover: the PNG is a complete badge and must not be cropped.
     The old scale(1.12) existed to push a JPG's white corners outside the
     circular clip — with no clip it would just crop the artwork's ring. */
  width: 100%; height: 100%; display: block; object-fit: contain;
}
.mco-tail {
  position: absolute; left: 50%; top: ${TAIL_Y}px;
  width: 0; height: 0; margin-left: -6px;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  /* matched to the artwork's own green ring rather than gold, so the anchor
     reads as part of the badge instead of a stray pointer */
  border-top: ${TAIL}px solid ${BRAND.leaf};
  filter: drop-shadow(0 2px 2px rgba(21,40,29,0.35));
}
.mco-pulse {
  position: absolute; left: 0; top: 0;
  width: ${RING}px; height: ${RING}px;
  border-radius: 50%; border: 2px solid ${BRAND.gold};
  animation: mcoPulse 2.2s ease-out infinite;
}
.mco-marker.mco-stale .mco-pulse { display: none; }
/* the ring has no border of its own any more — a stale fix reads through the
   greyed artwork and a muted tail instead */
.mco-marker.mco-stale .mco-tail { border-top-color: #9CA3AF; }
.mco-marker.mco-stale .mco-art  { filter: grayscale(0.6); }
.mco-dest svg { display: block; filter: drop-shadow(0 3px 5px rgba(21,40,29,0.3)); }
.mco-route { animation: mcoDash 1.1s linear infinite; }
@keyframes mcoPulse {
  0%   { transform: scale(0.9); opacity: 0.6; }
  70%  { transform: scale(1.5); opacity: 0; }
  100% { transform: scale(1.5); opacity: 0; }
}
@keyframes mcoDash {
  to { stroke-dashoffset: -${DASH_SPAN}; }
}
@media (prefers-reduced-motion: reduce) {
  .mco-pulse, .mco-route { animation: none; }
  .mco-rot { transition: none; }
}
`;

/* ═══════════════════════════════════════════════════════════════
   LIVE COURIER MAP
   Mr MilliyO rides the map: the marker is created once and then moved,
   so a new fix slides him along instead of snapping him into place.
═══════════════════════════════════════════════════════════════ */
const CourierMap = ({ lat, lng, destLat, destLng, stale = false, height = 300 }) => {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const courierRef   = useRef(null);
  const destRef      = useRef(null);
  const routeRef     = useRef(null);
  const prevPosRef   = useRef(null);
  const fittedRef    = useRef(false);
  const followRef    = useRef(true);   /* false once the operator takes over */
  const selfUntilRef = useRef(0);      /* our own fitBounds / panTo until    */

  const hasDest = Number.isFinite(destLat) && Number.isFinite(destLng);

  const courierEl = () => (courierRef.current ? courierRef.current.getElement() : null);

  /* Our own view changes must not read as "the operator moved the map".
     An animated zoom defers its movestart/zoomstart into a requestAnimationFrame,
     so a flag lifted the moment fitBounds returns is already down when the event
     lands — every reframe would then cancel auto-follow on its own. The guard is
     a deadline instead, held open past the zoom animation. */
  const selfView = (fn) => {
    selfUntilRef.current = Date.now() + SELF_VIEW_MS;
    fn();
  };

  const frameView = (L, map, pos) => {
    if (hasDest) {
      map.fitBounds(L.latLngBounds([pos, L.latLng(destLat, destLng)]), {
        padding: [46, 46], maxZoom: 16,
      });
    } else {
      map.setView(pos, 16);
    }
  };

  const recenter = () => {
    const L = window.L;
    const map = mapRef.current;
    if (!L || !map || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
    followRef.current = true;
    selfView(() => frameView(L, map, L.latLng(lat, lng)));
  };

  /* Tear the map down on unmount — otherwise re-opening an order throws
     "Map container is already initialized". */
  useEffect(() => () => {
    if (mapRef.current) mapRef.current.remove();
    mapRef.current     = null;
    courierRef.current = null;
    destRef.current    = null;
    routeRef.current   = null;
    prevPosRef.current = null;
    fittedRef.current  = false;
    followRef.current  = true;
    selfUntilRef.current = 0;
  }, []);

  useEffect(() => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;
    let dead = false;

    ensureLeafletLoaded().then((L) => {
      if (dead || !L || !containerRef.current) return;

      /* ── build the map once ── */
      if (!mapRef.current) {
        const map = L.map(containerRef.current, {
          zoomControl: true,
          attributionControl: true,
        }).setView([lat, lng], 15);
        mapRef.current = map;

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "© OpenStreetMap contributors",
          maxZoom: 19,
        }).addTo(map);

        /* Stop auto-framing the moment the operator pans or zooms */
        map.on("movestart zoomstart", () => {
          if (Date.now() >= selfUntilRef.current) followRef.current = false;
        });

        /* Leaflet animates markers itself while zooming — our glide would
           fight it, so hand the transform back to the stylesheet mid-zoom. */
        map.on("zoomstart", () => glide(courierEl(), false));
        map.on("zoomend", () => {
          const el = courierEl();
          window.requestAnimationFrame(() => glide(el, true));
        });

        selfView(() => map.invalidateSize(false));
      }

      const map = mapRef.current;
      const pos = L.latLng(lat, lng);

      /* ── courier: created once, then MOVED so it glides ── */
      if (!courierRef.current) {
        courierRef.current = L.marker(pos, {
          icon: L.divIcon({
            className: "mco-marker",
            html: COURIER_HTML,
            iconSize:   [ICON_W, ICON_H],
            iconAnchor: [ICON_W / 2, ICON_H],
          }),
          interactive: false,
          keyboard: false,
          zIndexOffset: 1000,
        }).addTo(map);
        glide(courierEl(), true);
      } else {
        const prev = prevPosRef.current;
        if (prev && map.distance(prev, pos) > MIN_MOVE_M) {
          applyHeading(courierEl(), bearingBetween(prev, pos));
        }
        courierRef.current.setLatLng(pos);
      }
      prevPosRef.current = pos;

      const el = courierEl();
      if (el) {
        if (stale) L.DomUtil.addClass(el, "mco-stale");
        else       L.DomUtil.removeClass(el, "mco-stale");
      }

      /* ── destination pin + the dashed lane leading to it ── */
      if (hasDest) {
        const dest = L.latLng(destLat, destLng);

        if (!destRef.current) {
          destRef.current = L.marker(dest, {
            icon: L.divIcon({
              className: "mco-destmarker",
              html: DEST_HTML,
              iconSize:   [30, 38],
              iconAnchor: [15, 38],
            }),
            interactive: false,
            keyboard: false,
          }).addTo(map);
        } else {
          destRef.current.setLatLng(dest);
        }

        if (!routeRef.current) {
          routeRef.current = L.polyline([pos, dest], {
            className: "mco-route",
            color: BRAND.gold, weight: 3, opacity: 0.9,
            dashArray: "8 10", lineCap: "round", interactive: false,
          }).addTo(map);
        } else {
          routeRef.current.setLatLngs([pos, dest]);
        }
      }

      /* ── framing: fit both pins once, then only nudge the view when the
         rider would otherwise leave the screen (and never after the
         operator has moved the map themselves) ── */
      if (!fittedRef.current) {
        fittedRef.current = true;
        selfView(() => frameView(L, map, pos));
      } else if (followRef.current && !map.getBounds().pad(-0.18).contains(pos)) {
        selfView(() => map.panTo(pos, { animate: true, duration: 0.6 }));
      }
    });

    return () => { dead = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng, destLat, destLng, stale]);

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={containerRef}
        style={{
          width: "100%", height, borderRadius: 8,
          border: "1px solid var(--mly-border)", background: "var(--mly-surface-2)",
          overflow: "hidden", position: "relative", zIndex: 0,
        }}
      />

      {/* Lets the operator hand the view back to the rider after a manual pan */}
      <button
        type="button"
        onClick={recenter}
        title="Frame the delivery partner and the drop point"
        style={{
          position: "absolute", top: 10, right: 10, zIndex: 600,
          display: "flex", alignItems: "center", gap: 5,
          padding: "5px 11px", borderRadius: 20,
          border: `1px solid ${BRAND.honey}`,
          background: "rgba(255,255,255,0.94)",
          color: BRAND.forest, fontSize: 11, fontWeight: 700,
          cursor: "pointer", boxShadow: "0 2px 6px rgba(21,40,29,0.18)",
        }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 1.4a1.1 1.1 0 0 1 1.1 1.1v1.2a8.1 8.1 0 0 1 7.2 7.2h1.2a1.1 1.1 0 0 1 0 2.2h-1.2a8.1 8.1 0 0 1-7.2 7.2v1.2a1.1 1.1 0 0 1-2.2 0v-1.2a8.1 8.1 0 0 1-7.2-7.2H2.5a1.1 1.1 0 0 1 0-2.2h1.2a8.1 8.1 0 0 1 7.2-7.2V2.5A1.1 1.1 0 0 1 12 1.4Zm0 4.6a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm0 3.2a2.8 2.8 0 1 1 0 5.6 2.8 2.8 0 0 1 0-5.6Z" /></svg>
        Recenter
      </button>

      <style>{MAP_CSS}</style>
    </div>
  );
};

export default CourierMap;
